import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SectionTitle } from "@/components/dashboard/widgets";
import { Briefcase, Mail, Linkedin, Loader2, Inbox, ExternalLink } from "lucide-react";
import { RowBin, ClearArea } from "@/components/admin/Purge";
import { toast } from "sonner";

/**
 * /admin/careers — Manage careers applications.
 * Read-mostly list with status & notes per application. Visual is
 * deliberately spartan and operator-focused — admins want signal not flash.
 */
const STATUS_OPTIONS = [
  { value: "new",         label: "New",          color: "bg-[#0055FF]/10 text-[#0055FF] border-[#0055FF]/20" },
  { value: "shortlisted", label: "Shortlisted",  color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { value: "interviewing",label: "Interviewing", color: "bg-amber-50 text-amber-700 border-amber-200" },
  { value: "offered",     label: "Offered",      color: "bg-violet-50 text-violet-700 border-violet-200" },
  { value: "rejected",    label: "Rejected",     color: "bg-slate-100 text-slate-600 border-slate-200" },
];

export default function AdminCareers() {
  const [rows, setRows] = useState(null);
  const [activeId, setActiveId] = useState(null);

  const refresh = async () => {
    try {
      const r = await api.get("/admin/careers/applications");
      setRows(r.data?.items || []);
    } catch (e) {
      toast.error("Could not load applications");
    }
  };
  useEffect(() => { refresh(); }, []);

  const updateRow = async (id, patch) => {
    try {
      await api.patch(`/admin/careers/applications/${id}`, patch);
      toast.success("Updated.");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const active = rows?.find(r => r.id === activeId);

  return (
    <div className="space-y-6">
      <SectionTitle
        title="Careers applications"
        subtitle="Inbound applications from /careers. Set status and notes per application."
        actions={<ClearArea area="careers" onDone={refresh} label="Clear applications" />}
      />

      {!rows && (
        <div className="text-center py-16 text-sm text-slate-500"><Loader2 className="size-4 inline animate-spin mr-2" /> Loading…</div>
      )}

      {rows && rows.length === 0 && (
        <Card className="border-slate-200">
          <CardContent className="py-16 text-center">
            <Inbox className="size-10 text-slate-300 mx-auto" />
            <h3 className="mt-3 text-sm font-semibold text-slate-900">No applications yet</h3>
            <p className="mt-1 text-xs text-slate-500 max-w-sm mx-auto">
              Once candidates apply through <a href="/careers" className="underline">/careers</a>, they'll appear here.
            </p>
          </CardContent>
        </Card>
      )}

      {rows && rows.length > 0 && (
        <div className="grid lg:grid-cols-3 gap-4">
          {/* List */}
          <div className="lg:col-span-1 space-y-2">
            {rows.map(r => {
              const meta = STATUS_OPTIONS.find(s => s.value === (r.status || "new"));
              return (
                <button
                  key={r.id}
                  onClick={() => setActiveId(r.id)}
                  data-testid={`career-app-${r.id}`}
                  className={`w-full text-left rounded-md border p-3 transition-colors ${activeId === r.id ? "border-slate-900 bg-slate-50" : "border-slate-200 bg-white hover:border-slate-300"}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-[13px] font-semibold text-slate-900 truncate">{r.name}</div>
                      <div className="text-[11.5px] text-slate-500 truncate font-mono">{r.role_title}</div>
                    </div>
                    <Badge variant="outline" className={`text-[9.5px] uppercase tracking-wide font-medium ${meta?.color}`}>
                      {meta?.label || r.status}
                    </Badge>
                  </div>
                  <div className="mt-2 text-[10.5px] text-slate-400 font-mono">
                    {new Date(r.created_at).toLocaleDateString("en-GB")} · {r.email}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Detail */}
          <div className="lg:col-span-2">
            {!active ? (
              <Card className="border-slate-200 border-dashed">
                <CardContent className="py-16 text-center text-sm text-slate-500">
                  <Briefcase className="size-8 text-slate-300 mx-auto mb-3" />
                  Select an application to review
                </CardContent>
              </Card>
            ) : (
              <Card className="border-slate-200">
                <CardContent className="p-6 space-y-5">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-mono">{active.role_title}</div>
                    <h2 className="mt-1 text-2xl font-semibold text-slate-900 tracking-tight">{active.name}</h2>
                    <div className="mt-2.5 flex items-center gap-3 text-[12.5px] text-slate-600 flex-wrap">
                      <a href={`mailto:${active.email}`} className="inline-flex items-center gap-1.5 hover:text-slate-900">
                        <Mail className="size-3" /> {active.email}
                      </a>
                      {active.linkedin && (
                        <>
                          <span className="text-slate-300">·</span>
                          <a href={active.linkedin} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 hover:text-slate-900">
                            <Linkedin className="size-3" /> Profile <ExternalLink className="size-2.5" />
                          </a>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-[10.5px] uppercase tracking-[0.14em] text-slate-500 font-mono mb-1.5">Status</div>
                      <Select value={active.status || "new"} onValueChange={(v) => updateRow(active.id, { status: v })}>
                        <SelectTrigger className="h-9 text-[13px]" data-testid="career-status-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <div className="text-[10.5px] uppercase tracking-[0.14em] text-slate-500 font-mono mb-1.5">Submitted</div>
                      <div className="text-[13px] text-slate-700 h-9 inline-flex items-center">{new Date(active.created_at).toLocaleString("en-GB")}</div>
                    </div>
                  </div>

                  <div>
                    <div className="text-[10.5px] uppercase tracking-[0.14em] text-slate-500 font-mono mb-1.5">Their pitch</div>
                    <div className="rounded-sm border border-slate-100 bg-slate-50/60 p-4 text-[13.5px] text-slate-700 leading-relaxed whitespace-pre-wrap">
                      {active.message}
                    </div>
                  </div>

                  <div>
                    <div className="text-[10.5px] uppercase tracking-[0.14em] text-slate-500 font-mono mb-1.5">Internal notes</div>
                    <Textarea
                      defaultValue={active.notes || ""}
                      rows={3}
                      placeholder="Notes for the hiring panel…"
                      onBlur={(e) => { if ((e.target.value || "") !== (active.notes || "")) updateRow(active.id, { notes: e.target.value }); }}
                      data-testid="career-notes"
                    />
                  </div>

                  <div className="flex items-center gap-2 pt-2">
                    <Button size="sm" variant="outline" className="border-slate-200" asChild>
                      <a href={`mailto:${active.email}?subject=Re: ${encodeURIComponent(active.role_title)}`}>
                        <Mail className="size-3.5 mr-1.5" /> Reply
                      </a>
                    </Button>
                    <div className="ml-auto">
                      <RowBin area="careers" id={active.id} name={active.name}
                              onDone={() => { setActiveId(null); refresh(); }} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
